import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Flag, CheckCircle, Star, Trophy, Clock, Zap, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const weeklyMissions = [
  {
    id: 'quiz',
    title: 'Take the Personality Quiz',
    description: 'Discover what makes you unique!',
    xp: 50,
    icon: '\u{1F9E0}',
    action: '/ms/quiz',
    category: 'discover'
  },
  {
    id: 'career',
    title: 'Explore 3 Career Cards',
    description: 'Learn about cool jobs',
    xp: 30,
    icon: '\u{1F680}',
    action: '/ms/careers',
    category: 'explore'
  },
  {
    id: 'dream',
    title: 'Add a Dream to Your List',
    description: 'Dream big, no limits!',
    xp: 20,
    icon: '\u2B50',
    action: '/ms/dreams',
    category: 'adventure'
  },
  {
    id: 'learn',
    title: 'Watch a Learning Video',
    description: 'Learn something new today',
    xp: 25,
    icon: '\u{1F4DA}',
    action: '/ms/learn',
    category: 'learn'
  },
  {
    id: 'strength',
    title: 'Identify Your Superpowers',
    description: 'What makes you awesome?',
    xp: 40,
    icon: '\u{1F4AA}',
    action: '/ms/strengths',
    category: 'discover'
  }
];

export default function MSMissionsPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [completedMissions, setCompletedMissions] = useState([]);
  const [totalXP, setTotalXP] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMissionProgress();
  }, []);

  const fetchMissionProgress = async () => {
    try {
      const response = await axios.get(`${API}/missions/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompletedMissions(response.data?.completed || []);
      setTotalXP(response.data?.total_xp || 0);
    } catch (error) {
      setTotalXP(0);
      const saved = localStorage.getItem('ms_missions_completed');
      if (saved) setCompletedMissions(JSON.parse(saved));
    } finally {
      setLoading(false);
    }
  };

  const startMission = (mission) => {
    if (completedMissions.includes(mission.id)) return;
    navigate(mission.action);
  };

  const completedCount = completedMissions.length;
  const totalMissions = weeklyMissions.length;
  const progressPercent = (completedCount / totalMissions) * 100;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
            <Flag className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="missions-title">This Week's Quests</h1>
            <p className="text-slate-300">Complete missions to earn XP!</p>
          </div>
        </div>

        {/* XP Display */}
        <div className="glass-card p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-white font-bold text-xl" data-testid="total-xp">{totalXP} XP</p>
              <p className="text-slate-300 text-sm">Total earned</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-white font-semibold" data-testid="quest-progress">{completedCount}/{totalMissions}</p>
            <p className="text-slate-300 text-sm">Quests done</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="h-4 bg-slate-700 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-orange-500 to-red-500"
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          {completedCount === totalMissions && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mt-3 text-center"
            >
              <Trophy className="w-8 h-8 text-yellow-400 mx-auto mb-1" />
              <p className="text-yellow-400 font-semibold">All quests complete! You're amazing!</p>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Missions List */}
      <div className="space-y-4">
        {weeklyMissions.map((mission, index) => {
          const isCompleted = completedMissions.includes(mission.id);
          const missionColors = [
            'from-purple-500/20 to-pink-500/20 border-purple-500/30',
            'from-blue-500/20 to-cyan-500/20 border-blue-500/30',
            'from-amber-500/20 to-orange-500/20 border-amber-500/30',
            'from-green-500/20 to-emerald-500/20 border-green-500/30',
            'from-indigo-500/20 to-purple-500/20 border-indigo-500/30'
          ];
          
          return (
            <motion.div
              key={mission.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              data-testid={`mission-card-${mission.id}`}
              className={`p-4 rounded-2xl border cursor-pointer transition-all hover:scale-[1.01] ${
                isCompleted 
                  ? 'bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-500/30' 
                  : `bg-gradient-to-br ${missionColors[index % missionColors.length]}`
              }`}
              onClick={() => !isCompleted && startMission(mission)}
            >
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-3xl ${
                  isCompleted ? 'bg-green-500/30' : 'bg-white/10'
                }`}>
                  {isCompleted ? <CheckCircle className="w-8 h-8 text-green-400" /> : mission.icon}
                </div>
                
                <div className="flex-1">
                  <h3 className={`font-bold ${isCompleted ? 'text-green-300' : 'text-white'}`}>
                    {mission.title}
                  </h3>
                  <p className="text-slate-300 text-sm">{mission.description}</p>
                </div>

                <div className="text-right flex flex-col items-end gap-2">
                  <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                    isCompleted 
                      ? 'bg-green-500/30 text-green-300' 
                      : 'bg-yellow-500/30 text-yellow-300'
                  }`}>
                    {isCompleted ? 'Done!' : `+${mission.xp} XP`}
                  </div>
                  
                  {!isCompleted && (
                    <Button
                      size="sm"
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid={`start-quest-${mission.id}`}
                    >
                      Go <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Reset Info */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-8 text-center"
      >
        <div className="flex items-center justify-center gap-2 text-slate-300">
          <Clock className="w-4 h-4" />
          <p className="text-sm">Quests reset every week. Keep coming back!</p>
        </div>
      </motion.div>
    </div>
  );
}
